import {useCallback, useMemo, useState} from 'react';

interface UseColumnVisibilityResult {
  hiddenColumns: Set<string>;
  visibleColumns: Array<string>;
  handleColumnToggle: (selectedColumn: string) => void;
  handleHideAll: () => void;
  handleShowAll: () => void;
}

export default function useColumnVisibility(allColumns: Array<string>): UseColumnVisibilityResult {
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set<string>());

  const visibleColumns = useMemo(
    () => allColumns.filter((col) => !hiddenColumns.has(col)),
    [allColumns, hiddenColumns]
  );

  const handleColumnToggle = useCallback(
    (selectedColumn: string) => {
      const newHiddenColumns = new Set(hiddenColumns);
      if (hiddenColumns.has(selectedColumn)) {
        // Show the column (remove from hidden)
        newHiddenColumns.delete(selectedColumn);
      } else {
        // Hide the column (add to hidden)
        newHiddenColumns.add(selectedColumn);
      }
      setHiddenColumns(newHiddenColumns);
    },
    [hiddenColumns]
  );

  const handleHideAll = useCallback(() => {
    setHiddenColumns(new Set(allColumns));
  }, [allColumns]);

  const handleShowAll = useCallback(() => {
    setHiddenColumns(new Set());
  }, []);

  return {
    hiddenColumns,
    visibleColumns,
    handleColumnToggle,
    handleHideAll,
    handleShowAll,
  };
}
